/**
 * ActivityCard — renders one activity suggestion on the dashboard
 * or child drill-down.
 *
 * Shows: title, subtitle, category badge, duration, peak moment
 * teaser, and action buttons (Accept / Skip). When completed,
 * shows a 5-star rating row.
 */

import { useState } from "react";

import { downloadIcsFile } from "@/lib/icsExport";
import {
  usePersonalizeSuggestion,
  useUpdateSuggestion,
  type ActivitySuggestion,
} from "@/features/activities/useActivities";

const CATEGORY_LABELS: Record<string, string> = {
  nature: "Nature",
  cooking: "Cooking",
  creative: "Creative",
  mastery: "Mastery",
  ritual: "Ritual",
  adventure: "Adventure",
  service: "Service",
  storytelling: "Storytelling",
  physical: "Physical",
  one_on_one: "One-on-one",
};

interface ActivityCardProps {
  suggestion: ActivitySuggestion;
  compact?: boolean | undefined;
}

export function ActivityCard({ suggestion, compact = false }: ActivityCardProps) {
  const { template } = suggestion;
  const description = suggestion.personalized_description ?? template.description;
  const peakMoment = suggestion.personalized_peak_moment ?? template.peak_moment;
  const update = useUpdateSuggestion();
  const personalize = usePersonalizeSuggestion();
  const isPersonalized = suggestion.personalized_description !== null;
  const [showDetails, setShowDetails] = useState(false);
  const [rating, setRating] = useState<number>(0);
  const [notes, setNotes] = useState("");
  const [showRating, setShowRating] = useState(false);
  const [planDate, setPlanDate] = useState(suggestion.suggested_for ?? "");
  const [planTime, setPlanTime] = useState("");

  const handleAccept = () => {
    update.mutate({ suggestionId: suggestion.id, status: "accepted" });
  };

  const handleSkip = () => {
    update.mutate({ suggestionId: suggestion.id, status: "skipped" });
  };

  const handleSnooze = () => {
    update.mutate({ suggestionId: suggestion.id, status: "snoozed" });
  };

  const handleComplete = () => {
    setShowRating(true);
  };

  const handleSaveDate = () => {
    if (planDate.length > 0) {
      update.mutate({ suggestionId: suggestion.id, suggested_for: planDate });
    }
  };

  const handleAddToCalendar = () => {
    if (planDate.length === 0) return;
    downloadIcsFile({
      title: `MIC: ${template.title}`,
      description: [
        description,
        "",
        `Peak moment: ${peakMoment}`,
        "",
        `After: ${template.reminiscing_prompt}`,
      ].join("\n"),
      date: planDate,
      time: planTime.length > 0 ? planTime : undefined,
      durationMinutes: template.duration_minutes ?? 60,
    });
  };

  const submitRating = () => {
    update.mutate({
      suggestionId: suggestion.id,
      status: "completed",
      rating: rating > 0 ? rating : undefined,
      notes: notes.trim() || undefined,
    });
    setShowRating(false);
  };

  const isActive = suggestion.status === "suggested" || suggestion.status === "accepted";
  const isCompleted = suggestion.status === "completed";

  return (
    <div className="rounded-lg bg-surface border border-edge px-4 md:px-5 py-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-sans text-[10px] uppercase tracking-[0.14em] font-semibold text-coral-deep bg-coral-soft/60 border border-coral-deep/20 rounded-pill px-2 py-0.5">
              {CATEGORY_LABELS[template.category] ?? template.category}
            </span>
            {template.duration_minutes !== null && (
              <span className="font-sans text-[11px] text-ink-muted">
                {template.duration_minutes} min
              </span>
            )}
            <span className="font-sans text-[11px] text-ink-muted">
              {template.indoor_outdoor === "either"
                ? "indoor or outdoor"
                : template.indoor_outdoor}
            </span>
          </div>
          <div className="font-serif text-[17px] md:text-[18px] text-ink leading-snug">
            {template.title}
          </div>
          {template.subtitle !== null && (
            <div className="font-sans text-[13px] text-ink-muted italic mt-0.5">
              {template.subtitle}
            </div>
          )}
          {isActive && !isPersonalized && (
            <button
              type="button"
              onClick={() => personalize.mutate({ suggestionId: suggestion.id })}
              disabled={personalize.isPending}
              className="self-start mt-1.5 font-sans text-[12px] text-coral-deep hover:underline disabled:opacity-50"
            >
              {personalize.isPending ? "Personalizing..." : "Personalize for my family"}
            </button>
          )}
          {isPersonalized && (
            <span className="self-start mt-1.5 font-sans text-[10px] uppercase tracking-[0.14em] text-amber-deep bg-amber-soft/50 border border-amber/20 rounded-pill px-2 py-0.5">
              Personalized
            </span>
          )}
        </div>

        {isCompleted && suggestion.rating !== null && (
          <div className="font-mono tabular-nums text-[18px] text-amber-deep shrink-0">
            {"★".repeat(suggestion.rating)}
            {"☆".repeat(5 - suggestion.rating)}
          </div>
        )}
      </div>

      {(showDetails || !compact) && (
        <div className="flex flex-col gap-3">
          <p className="font-sans text-[14px] text-ink-soft leading-[1.6]">
            {description}
          </p>

          <div className="rounded-md bg-amber-soft/40 border border-amber/20 px-4 py-3">
            <div className="font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-amber-deep mb-1">
              The moment to watch for
            </div>
            <div className="font-sans text-[13px] text-ink-soft italic">
              {peakMoment}
            </div>
          </div>

          <div className="rounded-md bg-coral-soft/30 border border-coral-deep/15 px-4 py-3">
            <div className="font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-coral-deep mb-1">
              Talk about it after
            </div>
            <div className="font-sans text-[13px] text-ink-soft italic">
              {template.reminiscing_prompt}
            </div>
          </div>
        </div>
      )}

      {compact && !showDetails && (
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="self-start font-sans text-[12px] text-coral-deep hover:underline"
        >
          Show details
        </button>
      )}

      {showRating && (
        <div className="flex flex-col gap-3 pt-1">
          <div className="flex items-center gap-4">
            <div className="font-sans text-[13px] text-ink-muted">How was it?</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`text-[22px] ${
                    star <= rating ? "text-amber-deep" : "text-ink-dim"
                  }`}
                >
                  {star <= rating ? "★" : "☆"}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What happened? What was the best part? (optional — this shows up in your journal)"
            maxLength={2000}
            rows={2}
            className="font-sans text-[14px] text-ink bg-surface border border-edge rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber resize-y"
          />
          <button
            type="button"
            onClick={submitRating}
            className="self-start px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white bg-coral-deep"
          >
            Save to journal
          </button>
        </div>
      )}

      {isActive && !showRating && (
        <div className="flex items-center gap-3 pt-1">
          {suggestion.status === "suggested" && (
            <button
              type="button"
              onClick={handleAccept}
              disabled={update.isPending}
              className="px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
                boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
              }}
            >
              {update.isPending ? "..." : "Let's do this"}
            </button>
          )}
          {suggestion.status === "accepted" && (
            <div className="flex flex-col gap-3 w-full">
              <div className="font-sans text-[12px] text-ink-muted font-medium">
                When are you planning to do this?
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="font-sans text-[11px] text-ink-muted uppercase tracking-[0.1em]">Date</span>
                    <input
                      type="date"
                      value={planDate}
                      onChange={(e) => setPlanDate(e.target.value)}
                      className="font-sans text-[14px] text-ink bg-bg border border-edge rounded-md px-3 py-2.5 min-w-[150px] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="font-sans text-[11px] text-ink-muted uppercase tracking-[0.1em]">Time <span className="normal-case italic">(optional)</span></span>
                    <input
                      type="time"
                      value={planTime}
                      onChange={(e) => setPlanTime(e.target.value)}
                      className="font-sans text-[14px] text-ink bg-bg border border-edge rounded-md px-3 py-2.5 min-w-[120px] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
                    />
                  </label>
                </div>
                {planDate.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveDate}
                      disabled={update.isPending}
                      className="px-4 py-2 rounded-md font-sans text-[13px] text-ink-muted border border-edge hover:border-coral-deep hover:text-coral-deep"
                    >
                      Save date
                    </button>
                    <button
                      type="button"
                      onClick={handleAddToCalendar}
                      className="px-4 py-2 rounded-md font-sans text-[13px] font-medium text-coral-deep border border-coral-deep/30 hover:bg-coral-soft/30"
                    >
                      Add to calendar
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={update.isPending}
                  className="px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white disabled:opacity-60"
                  style={{
                    background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
                    boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
                  }}
                >
                  We did it
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleSkip}
            disabled={update.isPending}
            className="px-4 py-2 rounded-md font-sans text-[13px] text-ink-muted hover:text-coral-deep"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleSnooze}
            disabled={update.isPending}
            className="px-4 py-2 rounded-md font-sans text-[12px] text-ink-dim hover:text-coral-deep"
            title="Won't suggest this activity for 6 months"
          >
            Not for us
          </button>
        </div>
      )}
    </div>
  );
}
